import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, ExternalLink, FileText, Link as LinkIcon } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { getDownloads, getImportantLinks } from "../lib/api";

export default function Resources() {
  const [downloads, setDownloads] = useState([]);
  const [importantLinks, setImportantLinks] = useState([]);

  useEffect(() => {
    getDownloads().then(setDownloads).catch(() => {});
    getImportantLinks().then(setImportantLinks).catch(() => {});
  }, []);

  return (
    <div data-testid="resources-page">
      <PageHeader
        eyebrow="Student Resources"
        title="Downloads & Important Links"
        subtitle="Find prospectus, admission forms, fee structure, syllabus, notices, exam documents, and useful official portals in one place."
      />

      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <div className="eyebrow mb-2">Downloads</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
              College documents and forms.
            </h2>
          </div>

          {downloads.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="downloads-grid">
              {downloads.map((item, i) => (
                <motion.a
                  key={item.id}
                  href={item.file_url}
                  target="_blank"
                  rel="noreferrer"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className="bg-white rounded-3xl p-6 border border-brand-primary/10 lift-card flex flex-col min-h-[220px]"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-primary text-brand-secondary flex items-center justify-center mb-5">
                    <FileText className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-2xl text-brand-primary leading-tight mb-2">
                    {item.title}
                  </h3>
                  {item.category && (
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-3">
                      {item.category}
                    </div>
                  )}
                  <p className="text-sm text-brand-ink/70 leading-relaxed flex-1">
                    {item.description || "Open this document for details."}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary mt-5">
                    Download <Download className="w-4 h-4 text-brand-secondary" />
                  </span>
                </motion.a>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-10 text-center text-brand-muted border border-brand-primary/10">
              Downloads will appear here after they are added from the admin panel.
            </div>
          )}
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-brand-accent/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <div className="eyebrow mb-2">Important Links</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
              Official portals and useful resources.
            </h2>
          </div>

          {importantLinks.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4 lg:gap-5" data-testid="important-links-grid">
              {importantLinks.map((item, i) => (
                <motion.a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className="bg-white rounded-3xl p-6 border border-brand-primary/10 lift-card flex items-start justify-between gap-5"
                >
                  <div className="flex gap-4">
                    <div className="w-11 h-11 rounded-full bg-brand-accent text-brand-primary flex items-center justify-center flex-shrink-0">
                      <LinkIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl text-brand-primary leading-tight">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-brand-ink/70 leading-relaxed mt-2">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                </motion.a>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-10 text-center text-brand-muted border border-brand-primary/10">
              Important links will appear here after they are added from the admin panel.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
